// Chakra imports
import {
  Portal,
  Box,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin';
import Sidebar from 'components/sidebar/Sidebar';
import { SidebarContext } from 'contexts/SidebarContext';
import { useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import routes from 'routes';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

// Mapeo externo de perfiles a rutas permitidas
const rolePermissions: Record<string, string[]> = {
  A1: [
    '/admin/plan-de-trabajo',
    '/admin/agenda',
    '/admin/usuarios',
    '/admin/sistema/turnos',
    '/admin/informe-de-actividades',
    '/admin/atencion-ciudadana',
    '/admin/gestion-juridica-legal',
    '/admin/gestion-contrataciones-licitaciones',
    '/admin/transparencia-rendicion-cuentas',
    '/admin/gestion-documentacion-legal',
    '/admin/gestion-documentos-correspondencia',
    '/admin/actas-minutas',
    '/admin/gestion-recursos-humanos',
    '/admin/control-documentacion-legal-administrativa',
    '/admin/planificacion-seguimiento-proyectos',
    '/admin/atencion-gestion-solicitudes-ciudadanas',
    '/admin/seguridad-control-acceso',
    '/admin/auditoria-interna',
    '/admin/control-finanzas-presupuesto',
    '/admin/gestion-contrataciones-licitaciones',
    '/admin/cumplimiento-normativo',
    '/admin/atencion-quejas-denuncias',
    '/admin/control-procedimientos-administrativos',
    '/admin/gestion-documentacion-registros',
    '/admin/Catalogos/catalogo',
    '/admin/sistema/usuarios',
    '/admin/sistema/s_iniciadas',
    '/admin/sistema/r_acciones',
    '/admin/tesoreria/propietarios',
    '/admin/tesoreria/ciudadanos',
    '/admin/tesoreria/predios',
    '/admin/tesoreria/transaccionescgeneral',
    '/admin/tesoreria/AguaPotable',
    '/admin/tesoreria/CFacturados',

    '/admin/tesoreria/transacciones',
    '/admin/tesoreria/tarifas',
    '/admin/tesoreria/recursos-humanos',
    '/admin/tesoreria/reportes-analisis-financiero',
    '/admin/tesoreria/control-deuda',
    '/admin/tesoreria/gestion-inversiones',
    '/admin/tesoreria/gestion-subsidios-subvenciones',
    '/admin/tesoreria/gestion-activos-fijos',
    '/admin/tesoreria/control-contratos-pagos',
    '/admin/tesoreria/gestion-impuestos-contribuciones',
    '/admin/tesoreria/recaudacion',
  ],
  CA1: ['/admin/tesoreria/AguaPotable','/admin/tesoreria/propietarios'],
  C1:['/admin/tesoreria/predios', '/admin/usuarios','/admin/tesoreria/transaccionescgeneral','/admin/tesoreria/propietarios',],
  U1:['/admin/comerciales','/admin/predios','/admin/agua-potable','/admin/multas']
};


// Custom Chakra theme
export default function Dashboard(props: { [x: string]: any }) {
  const { ...rest } = props;
  
  // Obtener el perfil del usuario desde Redux
  const user = useSelector((state: RootState) => state.auth.user);
  const userProfile = user?.rol || 'guest'; // Perfil del usuario actual
  console.log(user?.rol)

  // Obtener rutas permitidas según el perfil del usuario
  const getAllowedRoutes = (routes: RoutesType[], profile: string) => {
    const allowedPaths = rolePermissions[profile] || [];
    return routes
      .map((route) => {
        if (route.collapse && route.items) {
          // Filtrar subrutas
          const filteredItems = route.items.filter((item) =>
            allowedPaths.includes(item.layout + item.path)
          );
          if (filteredItems.length > 0) {
            return { ...route, items: filteredItems };
          }
          return null; // Omitir ruta si no tiene subrutas visibles
        }
        // Validar ruta principal
        return allowedPaths.includes(route.layout + route.path) ? route : null;
      })
      .filter(Boolean); // Eliminar rutas nulas
  };

  const allowedRoutes = getAllowedRoutes(routes, userProfile);

  // states and functions
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [mini, setMini] = useState(false);
  const [hovered, setHovered] = useState(false);

  const getRoute = () => {
    return window.location.pathname !== '/admin/full-screen-maps';
  };

  const getActiveRoute = (routes: RoutesType[]): string => {
    let activeRoute = '';
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routes: RoutesType[]): boolean => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routes: RoutesType[]): any => {
    return routes.map((route: RoutesType, key: number) => {
      if (route.layout === '/admin') {
        return (
          <Route path={`${route.path}`} element={route.component} key={key} />
        );
      }
      if (route.collapse) {
        return getRoutes(route.items);
      } else {
        return null;
      }
    });
  };

  const { onOpen } = useDisclosure();
  const bg = useColorModeValue('background.100', 'background.900');

  return (
    <Box bg={bg} h="100vh" w="100vw">
      <SidebarContext.Provider
        value={{
          toggleSidebar,
          setToggleSidebar,
        }}
      >
        <Sidebar
          hovered={hovered}
          setHovered={setHovered}
          mini={mini}
          routes={allowedRoutes} // Pasar las rutas filtradas al Sidebar
          display="none"
          {...rest}
        />
        <Box
          float="right"
          minHeight="100vh"
          height="100%"
          overflow="auto"
          position="relative"
          maxHeight="100%"
          w={
            mini === false
              ? { base: '100%', xl: 'calc( 100% - 290px )' }
              : mini === true && hovered === true
              ? { base: '100%', xl: 'calc( 100% - 290px )' }
              : { base: '100%', xl: 'calc( 100% - 120px )' }
          }
          maxWidth={
            mini === false
              ? { base: '100%', xl: 'calc( 100% - 290px )' }
              : mini === true && hovered === true
              ? { base: '100%', xl: 'calc( 100% - 290px )' }
              : { base: '100%', xl: 'calc( 100% - 120px )' }
          }
          transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
          transitionDuration=".2s, .2s, .35s"
          transitionProperty="top, bottom, width"
          transitionTimingFunction="linear, linear, ease"
        >
          <Portal>
            <Box>
              <Navbar
                hovered={hovered}
                setMini={setMini}
                mini={mini}
                onOpen={onOpen}
                logoText={'Horizon UI Dashboard PRO'}
                brandText={getActiveRoute(allowedRoutes)} // Usar rutas filtradas
                secondary={getActiveNavbar(allowedRoutes)} // Usar rutas filtradas
                theme={props.theme}
                setTheme={props.setTheme}
                fixed={fixed}
                {...rest}
              />
            </Box>
          </Portal>

          {getRoute() ? (
            <Box
              mx="auto"
              p={{ base: '20px', md: '30px' }}
              pe="20px"
              minH="100vh"
              pt="50px"
            >
              <Routes>
                {getRoutes(allowedRoutes)} {/* Usar rutas filtradas */}
                <Route
                  path="/"
                  element={<Navigate to="/admin/plan-de-trabajo" replace />}
                />
              </Routes>
            </Box>
          ) : null}
          <Box>
            <Footer />
          </Box>
        </Box>
      </SidebarContext.Provider>
      <Box></Box>
    </Box>
  );
}
